/**
 * Службы компании — настройка уведомлений о подписанных договорах (0259).
 *
 * Зачем: когда юрист вводит договор в работу, система раскладывает его
 * обязательства по службам и уведомляет людей. Какие службы есть, что каждая
 * ведёт и кто в неё входит — решает компания, а не разработчик: где-то платит
 * бухгалтерия, где-то финансовый отдел.
 *
 * Служба без сотрудников уведомление формально получает, но читать его некому —
 * поэтому пустой состав здесь показан явным предупреждением, а не скрыт.
 */
import { useState } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import {
  useAddOrgServiceMemberMutation,
  useAdminListUsersQuery,
  useCreateOrgServiceMutation,
  useDeleteOrgServiceMutation,
  useListOrgServicesQuery,
  useRemoveOrgServiceMemberMutation,
  useUpdateOrgServiceMutation,
} from '@shared/api';
import { execRoleLabel, type ExecRole, type OrgService } from '@entities/notification/types';

const Intro = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 820px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const ServiceCard = styled(Card)<{ $off: boolean }>`
  padding: ${({ theme }) => theme.spacing(4)};
  opacity: ${({ $off }) => ($off ? 0.6 : 1)};
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Title = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Label = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  width: 100%;
`;

const Chip = styled.button<{ $on: boolean }>`
  padding: 5px 11px;
  border: 1px solid
    ${({ $on, theme }) => ($on ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ $on, theme }) => ($on ? theme.colors.primarySoft : 'transparent')};
  color: ${({ $on, theme }) => ($on ? theme.colors.primary : theme.colors.text.secondary)};
  font: inherit;
  font-size: 12.5px;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const Member = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px 4px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: 12.5px;
`;

const RemoveBtn = styled.button`
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.text.muted};
  font: inherit;
  cursor: pointer;
  line-height: 1;

  &:hover {
    color: ${({ theme }) => theme.colors.status.danger};
  }
`;

const Input = styled.input`
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;
  min-width: 220px;
`;

const Select = styled.select`
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;
`;

const Muted = styled.div`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Warn = styled.div`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.status.warning};
`;

export function AdminServicesTab() {
  const { data, isLoading } = useListOrgServicesQuery();
  const { data: usersData } = useAdminListUsersQuery({ active: true, limit: 200 });
  const [createService] = useCreateOrgServiceMutation();
  const [updateService] = useUpdateOrgServiceMutation();
  const [deleteService] = useDeleteOrgServiceMutation();
  const [addMember] = useAddOrgServiceMemberMutation();
  const [removeMember] = useRemoveOrgServiceMemberMutation();
  const [newTitle, setNewTitle] = useState('');

  const services = data?.items ?? [];
  const roles = data?.exec_roles ?? [];
  const users = usersData?.items ?? [];

  async function toggleRole(service: OrgService, role: ExecRole) {
    const next = service.exec_roles.includes(role)
      ? service.exec_roles.filter((r) => r !== role)
      : [...service.exec_roles, role];
    await updateService({
      id: service.id,
      body: {
        title: service.title,
        exec_roles: next,
        always_notify: service.always_notify,
        is_active: service.is_active,
        sort_order: service.sort_order,
        version: service.version,
      },
    }).unwrap();
  }

  async function patch(service: OrgService, patchBody: Partial<OrgService>) {
    await updateService({
      id: service.id,
      body: {
        title: patchBody.title ?? service.title,
        exec_roles: (patchBody.exec_roles ?? service.exec_roles) as ExecRole[],
        always_notify: patchBody.always_notify ?? service.always_notify,
        is_active: patchBody.is_active ?? service.is_active,
        sort_order: patchBody.sort_order ?? service.sort_order,
        version: service.version,
      },
    }).unwrap();
  }

  async function onCreate() {
    const title = newTitle.trim();
    if (!title) return;
    await createService({ title, exec_roles: [], always_notify: false }).unwrap();
    setNewTitle('');
  }

  if (isLoading) return <Muted>Загрузка служб…</Muted>;

  return (
    <>
      <Intro>
        Когда юрист вводит подписанный договор в работу, система раскладывает его обязательства по
        службам и шлёт каждой только её часть: оплаты — тем, кто платит, поставку — снабжению,
        приёмку — стройке. Здесь вы задаёте, какие службы есть, что каждая ведёт и кто в неё
        входит. Уведомление приходит в колокольчик и не требует прав на раздел «Юрист».
      </Intro>

      <Row style={{ marginBottom: 20 }}>
        <Input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="Название службы: Бухгалтерия, Механики, Финансовый отдел…"
          aria-label="Название новой службы"
        />
        <Button onClick={onCreate} disabled={!newTitle.trim()}>
          Добавить службу
        </Button>
      </Row>

      {services.length === 0 ? (
        <Muted>Служб пока нет. Добавьте первую — без них договор вводить в работу некому.</Muted>
      ) : (
        <List>
          {services.map((s) => {
            const members = (data?.members ?? []).filter((m) => m.service_id === s.id);
            const free = users.filter((u) => !members.some((m) => m.user_id === u.id));
            return (
              <ServiceCard key={s.id} $off={!s.is_active}>
                <Head>
                  <Title>{s.title}</Title>
                  <Row style={{ margin: 0 }}>
                    {!s.is_active && <Badge $tone="neutral">выключена</Badge>}
                    <Button
                      $variant="ghost"
                      $size="sm"
                      onClick={() => patch(s, { is_active: !s.is_active })}
                    >
                      {s.is_active ? 'Выключить' : 'Включить'}
                    </Button>
                    <Button $variant="danger" $size="sm" onClick={() => deleteService(s.id)}>
                      Удалить
                    </Button>
                  </Row>
                </Head>

                <Label>Какие обязательства ведёт служба</Label>
                <Row style={{ marginTop: 4 }}>
                  {roles.map((r) => (
                    <Chip
                      key={r}
                      type="button"
                      $on={s.exec_roles.includes(r)}
                      onClick={() => toggleRole(s, r)}
                    >
                      {execRoleLabel(r)}
                    </Chip>
                  ))}
                  <Chip
                    type="button"
                    $on={s.always_notify}
                    onClick={() => patch(s, { always_notify: !s.always_notify })}
                    title="Уведомлять обо всех договорах, даже когда своих обязательств нет"
                  >
                    Все договоры
                  </Chip>
                </Row>

                <Label style={{ marginTop: 12 }}>Кто получает уведомления</Label>
                <Row style={{ marginTop: 4 }}>
                  {members.length === 0 ? (
                    <Warn>Никого нет — уведомления этой службы читать некому.</Warn>
                  ) : (
                    members.map((m) => (
                      <Member key={m.user_id}>
                        {m.full_name}
                        {m.department ? ` · ${m.department}` : ''}
                        <RemoveBtn
                          type="button"
                          aria-label={`Убрать ${m.full_name}`}
                          onClick={() => removeMember({ id: s.id, user_id: m.user_id })}
                        >
                          ×
                        </RemoveBtn>
                      </Member>
                    ))
                  )}
                </Row>
                <Row>
                  <Select
                    defaultValue=""
                    aria-label={`Добавить сотрудника в службу «${s.title}»`}
                    onChange={(e) => {
                      if (e.target.value) {
                        addMember({ id: s.id, user_id: e.target.value });
                        e.target.value = '';
                      }
                    }}
                  >
                    <option value="">Добавить сотрудника…</option>
                    {free.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.full_name}
                        {u.department ? ` · ${u.department}` : ''}
                      </option>
                    ))}
                  </Select>
                </Row>
              </ServiceCard>
            );
          })}
        </List>
      )}
    </>
  );
}
